// src/redux/slices/applicationSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../helpers/axiosInstance';
import { extractErrorMessage } from '../helpers/errorMessage';

// ---------------------------------------------------------------
// Thunks — one per backend endpoint.
// Each PATCH returns the FULL updated application, so every
// fulfilled case replaces `current` wholesale. No local merging,
// no chance of the UI drifting from the server.
// ---------------------------------------------------------------

export const loadDraft = createAsyncThunk(
  'application/loadDraft',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.get('/applications/draft');
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const savePersonalInfo = createAsyncThunk(
  'application/savePersonalInfo',
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/applications/${id}/personal`, values);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const saveKycInfo = createAsyncThunk(
  'application/saveKycInfo',
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/applications/${id}/kyc`, values);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const saveLoanDetails = createAsyncThunk(
  'application/saveLoanDetails',
  async ({ id, values }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.patch(`/applications/${id}/loan`, values);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const uploadDocument = createAsyncThunk(
  'application/uploadDocument',
  async ({ id, docType, file }, { rejectWithValue }) => {
    try {
      const form = new FormData();
      form.append('docType', docType);
      form.append('file', file);

      // Let the browser set Content-Type — it must include the multipart
      // boundary, which we cannot generate ourselves.
      const { data } = await axiosInstance.post(`/applications/${id}/documents`, form, {
        headers: { 'Content-Type': undefined }
      });
      return { docType, document: data.data };
    } catch (error) {
      return rejectWithValue({ docType, message: extractErrorMessage(error) });
    }
  }
);

export const removeDocument = createAsyncThunk(
  'application/removeDocument',
  async ({ documentId }, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/documents/${documentId}`);
      return documentId;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const submitApplication = createAsyncThunk(
  'application/submit',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/applications/${id}/submit`, {
        consentGiven: true
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const extractFromPdf = createAsyncThunk(
  'application/extractFromPdf',
  async (file, { rejectWithValue }) => {
    try {
      const form = new FormData();
      form.append('file', file);

      const { data } = await axiosInstance.post('/extraction/extract', form, {
        headers: { 'Content-Type': undefined }
      });

      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const resetDraft = createAsyncThunk(
  'application/resetDraft',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axiosInstance.post(`/applications/${id}/reset`);
      return data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

// ---------------------------------------------------------------

const initialState = {
  current: null,
  loading: false, // full-page load
  saving: false, // step save in flight
  submitting: false,
  error: null,
  uploadingType: null, // which docType is mid-upload
  uploadError: null,
  extracting: false,
  extractedFields: null,
  extractMeta: null
};

// Shared handling for every step-save thunk.
const savePending = (state) => {
  state.saving = true;
  state.error = null;
};

const saveFulfilled = (state, action) => {
  state.saving = false;
  state.current = action.payload;
};

const saveRejected = (state, action) => {
  state.saving = false;
  state.error = action.payload;
  if (typeof action.payload === 'string' && action.payload.includes('not found')) {
    state.current = null;
  }
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,

  reducers: {
    clearApplicationError: (state) => {
      state.error = null;
      state.uploadError = null;
    },
    clearExtraction: (state) => {
      state.extractedFields = null;
      state.extractMeta = null;
    },
    resetApplication: () => initialState
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(loadDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(savePersonalInfo.pending, savePending)
      .addCase(savePersonalInfo.fulfilled, saveFulfilled)
      .addCase(savePersonalInfo.rejected, saveRejected)

      .addCase(saveKycInfo.pending, savePending)
      .addCase(saveKycInfo.fulfilled, saveFulfilled)
      .addCase(saveKycInfo.rejected, saveRejected)

      .addCase(saveLoanDetails.pending, savePending)
      .addCase(saveLoanDetails.fulfilled, saveFulfilled)
      .addCase(saveLoanDetails.rejected, saveRejected)

      .addCase(uploadDocument.pending, (state, action) => {
        state.uploadingType = action.meta.arg.docType;
        state.uploadError = null;
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        state.uploadingType = null;
        if (!state.current) return;

        const { docType, document } = action.payload;

        // Types that allow multiple files — append instead of replace.
        const MULTI_TYPES = ['SALARY_SLIP', 'BANK_STATEMENT', 'ITR'];

        if (MULTI_TYPES.includes(docType)) {
          // Append the new document, keep all existing.
          state.current.documents = [...state.current.documents, document];
        } else {
          // Single-file type — replace any existing of this type.
          const others = state.current.documents.filter((d) => d.docType !== docType);
          state.current.documents = [...others, document];
        }
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        state.uploadingType = null;
        state.uploadError = action.payload;
      })

      .addCase(removeDocument.fulfilled, (state, action) => {
        if (!state.current) return;
        state.current.documents = state.current.documents.filter((d) => d.id !== action.payload);
      })

      .addCase(submitApplication.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(submitApplication.fulfilled, (state, action) => {
        state.submitting = false;
        state.current = action.payload;
      })
      .addCase(submitApplication.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      .addCase(extractFromPdf.pending, (state) => {
        state.extracting = true;
      })
      .addCase(extractFromPdf.fulfilled, (state, action) => {
        state.extracting = false;
        state.extractedFields = action.payload.fields;
        state.extractMeta = action.payload.meta;
      })
      .addCase(extractFromPdf.rejected, (state) => {
        state.extracting = false;
      })

      .addCase(resetDraft.fulfilled, (state, action) => {
        state.current = action.payload;
        state.extractedFields = null; // clear any imported data too
        state.extractMeta = null;
      });
  }
});

export const { clearApplicationError, resetApplication, clearExtraction } =
  applicationSlice.actions;

// ---- Selectors ----
export const selectApplication = (state) => state.application.current;
export const selectAppLoading = (state) => state.application.loading;
export const selectAppSaving = (state) => state.application.saving;
export const selectAppSubmitting = (state) => state.application.submitting;
export const selectAppError = (state) => state.application.error;
export const selectUploadingType = (state) => state.application.uploadingType;
export const selectUploadError = (state) => state.application.uploadError;
export const selectExtracting = (state) => state.application.extracting;
export const selectExtractedFields = (state) => state.application.extractedFields;
export const selectExtractMeta = (state) => state.application.extractMeta;

export const selectDocumentsByType = (state) => {
  const docs = state.application.current?.documents ?? [];
  return docs.reduce((acc, doc) => {
    acc[doc.docType] = acc[doc.docType] ?? [];
    acc[doc.docType].push(doc);
    return acc;
  }, {});
};

export default applicationSlice.reducer;
